import { createGlobalStyle, css } from 'styled-components';

import { Theme } from 'utils/color/horizonTheme';

/**
 * This is global css passed a s a child of ThemeProvider in
 * `src/horizon/index.tsx`. It should contain only global styles that
 * don't fit nicely as a style of a specific component, e.g. scrollbars.
 *
 * TODO: consider moving appropriate items from App.css here.
 */

export const GlobalStyles = createGlobalStyle`
  ${({ theme }) =>
    theme.isHorizonGridVisualRefresh
      ? css`
          body {
            font-family: 'Inter', sans-serif !important;
            background: ${theme.primaryBackgroundColor};
          }

          /* Brand headlines use Open Sans; body copy stays Inter (Brand Guidelines V3). */
          h1,
          h2,
          h3,
          h4,
          h5,
          h6,
          .ant-typography h1,
          .ant-typography h2,
          .ant-typography h3,
          .ant-typography h4,
          .ant-typography h5,
          h1.ant-typography,
          h2.ant-typography,
          h3.ant-typography,
          h4.ant-typography,
          h5.ant-typography {
            font-family: 'Open Sans', sans-serif;
          }

          a:hover {
            color: ${theme.actionTextHover};
          }

          /*
           * antd v4 components derive their primary color from the build-time
           * LESS variable (still SkySpecs teal), which can't follow this runtime
           * toggle. Re-point the common v4 primary surfaces to the platform
           * action color so they match the antd v5 / styled-components UI.
           *
           * antd v4 and v5 share the 'ant' class prefix in this app, so these
           * also match v5 controls — harmless, since v5 already renders these in
           * theme.teal via ConfigProvider. The :not(...-disabled) guards keep
           * antd's muted disabled styling for both versions (and raise specificity
           * enough to win over v4's base rules without !important).
           */
          .ant-checkbox-checked:not(.ant-checkbox-disabled) .ant-checkbox-inner {
            background-color: ${theme.teal};
            border-color: ${theme.teal};
          }
          .ant-checkbox-wrapper:not(.ant-checkbox-wrapper-disabled):hover .ant-checkbox-inner,
          .ant-checkbox:not(.ant-checkbox-disabled):hover .ant-checkbox-inner,
          .ant-checkbox-input:focus + .ant-checkbox-inner {
            border-color: ${theme.teal};
          }
          .ant-checkbox-indeterminate:not(.ant-checkbox-disabled) .ant-checkbox-inner::after {
            background-color: ${theme.teal};
          }

          .ant-radio-checked:not(.ant-radio-disabled) .ant-radio-inner {
            border-color: ${theme.teal};
          }
          .ant-radio-checked:not(.ant-radio-disabled) .ant-radio-inner::after {
            background-color: ${theme.teal};
          }
          .ant-radio:not(.ant-radio-disabled):hover .ant-radio-inner,
          .ant-radio-input:focus + .ant-radio-inner {
            border-color: ${theme.teal};
          }

          .ant-switch-checked:not(.ant-switch-disabled) {
            background-color: ${theme.teal};
          }

          .ant-tabs-ink-bar {
            background: ${theme.teal};
          }
          .ant-tabs-tab:not(.ant-tabs-tab-disabled):hover,
          .ant-tabs-tab.ant-tabs-tab-active .ant-tabs-tab-btn {
            color: ${theme.actionText};
          }

          .ant-slider:not(.ant-slider-disabled) .ant-slider-track {
            background-color: ${theme.teal};
          }
          .ant-slider:not(.ant-slider-disabled) .ant-slider-handle {
            border-color: ${theme.teal};
          }

          .ant-pagination-item-active {
            border-color: ${theme.actionText};
          }
          .ant-pagination-item-active a {
            color: ${theme.actionText};
          }

          .ant-spin-dot-item {
            background-color: ${theme.teal};
          }
        `
      : ''}

  ${({ theme }) =>
    theme.themeName === Theme.Dark
      ? css`
          html,
          body {
            scrollbar-color: ${theme.borderColor} ${theme.tertiaryBackgroundColor};

            ::-webkit-scrollbar-track {
              background-color: ${theme.tertiaryBackgroundColor};
            }

            ::-webkit-scrollbar-thumb {
              background-color: ${theme.borderColor};
            }

            h1,
            h2,
            h3,
            h4,
            h5,
            h6 {
              color: ${theme.headerColor};
            }

            div,
            p,
            span,
            i,
            em,
            b,
            strong {
              color: ${theme.textColor};
            }

            button,
            a {
              div,
              p,
              span,
              i,
              em,
              b,
              strong {
                color: unset;
              }
            }

            label {
              color: ${theme.labelColor};
            }
          }
        `
      : ''}
`;
